import { forwardRef, type PropsWithChildren } from 'react';

import { Stack, IconButton, Select, Visible } from '@backyard-ui/core';
import { ChevronLeft, ChevronRight } from '@backyard-ui/icons';

import { type Order } from '@/infra/services/catalog/review/model';
import { algolia } from '@/infra/tracking/algolia';

import PageIndicator from './PageIndicator';
import * as Skeleton from './Skeleton';

interface PaginationProps extends PropsWithChildren {
  onNext: () => void;
  onPrev: () => void;
  onReorder: (order: Order) => void;
  currentPage: number;
  pageSize: number;
  orderItems: {
    name: string;
    value: Order;
  }[];
  isFetching: boolean;
  totalReviewsCount: number;
}

const Pagination = forwardRef<HTMLDivElement, PaginationProps>((props, ref) => {
  const {
    onNext,
    onPrev,
    onReorder,
    currentPage,
    pageSize,
    orderItems,
    isFetching,
    children,
    totalReviewsCount,
  } = props;

  const isInitialPage = currentPage <= 1;
  const hasMoreReviews = currentPage * pageSize < totalReviewsCount;

  const isPrevButtonDisabled = isInitialPage || isFetching;
  const isNextButtonDisabled = !hasMoreReviews || isFetching;

  return (
    <Stack direction="column" spacing="10" ref={ref}>
      <Stack
        direction={{ initial: 'column', md: 'row' }}
        spacing={{ initial: '3', md: '4' }}
        align={{ initial: 'stretch', md: 'center' }}
      >
        {isFetching ? (
          <Skeleton.PageIndicator />
        ) : (
          <PageIndicator
            totalReviewsCount={totalReviewsCount}
            currentPage={currentPage}
            pageSize={pageSize}
          />
        )}
        <span className="h-px flex-1 bg-black" />
        <Stack spacing="2" align="center">
          <Visible above="md">
            <span>Classificar por:</span>
          </Visible>
          <div className="w-full md:w-auto">
            <Select.Root
              defaultValue={orderItems[0].value}
              onValueChange={(value: Order) => {
                algolia.sendPdpClickEvent();
                onReorder(value);
              }}
            >
              <Select.Trigger aria-label="Classificar avaliações">
                <Select.Value />
              </Select.Trigger>

              <Select.Content>
                {orderItems.map(({ name, value }, index) => (
                  <Select.Item key={index} value={value} textValue={name}>
                    {name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
        </Stack>
      </Stack>
      {isFetching ? <Skeleton.Items /> : <div>{children}</div>}
      <Stack
        spacing={{ initial: '0', md: '4' }}
        align={{ initial: 'center', md: 'center' }}
        justify={{ initial: 'space-between', md: 'flex-start' }}
      >
        {isFetching ? (
          <Skeleton.PageIndicator />
        ) : (
          <PageIndicator
            totalReviewsCount={totalReviewsCount}
            currentPage={currentPage}
            pageSize={pageSize}
          />
        )}
        <Visible above="md">
          <span className="h-px flex-1 bg-black" />
        </Visible>
        <Stack spacing="2.5">
          <IconButton
            icon={<ChevronLeft />}
            size="xs"
            variant="outline"
            appearance="neutral"
            onClick={() => {
              algolia.sendPdpClickEvent();
              onPrev();
            }}
            disabled={isPrevButtonDisabled}
            data-testid="prev-button"
            aria-label="Ir para a página anterior"
          />
          <IconButton
            icon={<ChevronRight />}
            size="xs"
            variant="outline"
            appearance="neutral"
            onClick={() => {
              algolia.sendPdpClickEvent();
              onNext();
            }}
            disabled={isNextButtonDisabled}
            data-testid="next-button"
            aria-label="Ir para a próxima página"
          />
        </Stack>
      </Stack>
    </Stack>
  );
});

Pagination.displayName = 'Pagination';

export default Pagination;
